import { useState, useEffect, useCallback, useRef } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { AudioProvider } from '@/contexts/AudioContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import GlobalTranslator from '@/components/GlobalTranslator';
import MusicPlayer from '@/components/MusicPlayer';
import MobileWelcomeGate from '@/components/MobileWelcomeGate';
import PendingDeletionGate from '@/components/PendingDeletionGate';
import WelcomeBack from '@/pages/WelcomeBack';

// Pages
import HomePage from '@/pages/HomePage';
import DashboardLayout from '@/components/DashboardLayout';import ModulesPage from '@/pages/ModulesPage';
import MyAssessmentsPage from '@/pages/assessments/MyAssessmentsPage';
import CompanionPage from '@/pages/assessments/CompanionPage';
import CompanionSpace from '@/components/companion/CompanionSpace';
import AssessmentTakePage from '@/pages/assessments/AssessmentTakePage';
import AssessmentResultPage from '@/pages/assessments/AssessmentResultPage';
import PortalLanding from '@/pages/PortalLanding';
import PlatinumPlus from '@/pages/platinum/PlatinumPlus';
import ModuleDetailPage from '@/pages/ModuleDetailPage';
import SectionHomeworkFeed from '@/pages/SectionHomeworkFeed';
import ForumPage from '@/pages/ForumPage';
import CalendarPage from '@/pages/CalendarPage';
import CalendarGridPage from '@/pages/CalendarGridPage';
import DayEventsPage from '@/pages/DayEventsPage';
import ResourcesPage from '@/pages/ResourcesPage';
import LiveStreamPage from '@/pages/LiveStreamPage';
import ReplayArchivePage from '@/pages/ReplayArchivePage';
import AdminPanel from '@/pages/AdminPanelEnhanced';
import SettingsPage from '@/pages/SettingsPage';
import QAPage from '@/pages/QAPage';
import MyMembership from '@/pages/MyMembership';
import MessagesPage from '@/pages/MessagesPage';
import TermsOfService from '@/pages/TermsOfService';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import CommunityGuidelines from '@/pages/CommunityGuidelines';
import VendorTermsOfService from '@/pages/VendorTermsOfService';
import MarketplaceGuidelines from '@/pages/MarketplaceGuidelines';
import PractitionerAgreementDoc from '@/pages/PractitionerAgreementDoc';
import PublicBlogPage from '@/pages/PublicBlogPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import WorkshopsPage from '@/pages/WorkshopsPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';
import PaymentCancelPage from '@/pages/PaymentCancelPage';

// Marketplace Pages
import MarketplaceHome from '@/pages/marketplace/MarketplaceHome';
import BookingsPortal from '@/pages/marketplace/BookingsPortal';
import OrderHistory from '@/pages/marketplace/OrderHistory';
import VendorApplication from '@/pages/marketplace/VendorApplication';
import VendorProducts from '@/pages/marketplace/VendorProducts';
import VendorStripeOnboarding from '@/pages/marketplace/VendorStripeOnboarding';
import VendorApprovals from '@/pages/marketplace/VendorApprovals';
import PublicBookingPage from '@/pages/marketplace/PublicBookingPage';
import CartDrawer from '@/pages/marketplace/CartDrawer';
import VendorEarnings from '@/pages/marketplace/VendorEarnings';
import VendorOrders from '@/pages/marketplace/VendorOrders';
import VendorShipping from '@/pages/marketplace/VendorShipping';
import AdminDashboard from '@/pages/marketplace/AdminDashboard';
import VendorAvailability from '@/pages/marketplace/VendorAvailability';
import VendorBookings from '@/pages/marketplace/VendorBookings';
import BookingConfirmation from '@/pages/marketplace/BookingConfirmation';
import VendorReviews from '@/pages/marketplace/VendorReviews';
import VendorStorefront from '@/pages/marketplace/VendorStorefront';
import ProductDetail from '@/pages/marketplace/ProductDetail';
import ShelfPage from '@/pages/marketplace/ShelfPage';
import CartPage from '@/pages/marketplace/CartPage';
import AllVendorsPage from '@/pages/marketplace/AllVendorsPage';
import SessionReviewTip from '@/pages/marketplace/SessionReviewTip';
import MyBookings from '@/pages/marketplace/MyBookings';
import BookingsPage from '@/pages/BookingsPage';
import PractitionerHub from '@/pages/PractitionerHub';
import EventDetailPage from '@/pages/EventDetailPage';
import PractitionerOnboarding from '@/pages/PractitionerOnboarding';
import AuthCallback from '@/pages/AuthCallback';
import LinkGoogleCallback from '@/pages/LinkGoogleCallback';
import PractitionerInvitePage from '@/pages/PractitionerInvitePage';
import VendorInvitePage from '@/pages/VendorInvitePage';
import VendorOnboarding from '@/pages/VendorOnboarding';
import IntakeFormFillPage from '@/pages/IntakeFormFillPage';
import BookingProposalPage from '@/pages/BookingProposalPage';
import PrintIntakePage from '@/pages/practitioner/PrintIntakePage';
import IntakeFormsPage from '@/pages/practitioner/IntakeFormsPage';
import InviteGoogleCallback from '@/pages/InviteGoogleCallback';
import NotFoundPage from '@/pages/NotFoundPage';
import AdvertisePage from '@/pages/AdvertisePage';
import AdvertiserFormPage from '@/pages/AdvertiserFormPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // A View-As session the member withdrew, or that lapsed on its own: hand the admin
    // back their own session rather than leaving them stranded in a 403ing account.
    if (error.response?.status === 403 && error.response?.data?.view_as_ended) {
      const adminToken = localStorage.getItem('admin_token');
      if (adminToken) {
        localStorage.setItem('token', adminToken);
        localStorage.setItem('user', localStorage.getItem('admin_user') || '');
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        localStorage.removeItem('view_as');
        localStorage.removeItem('activeVendorId');
        window.location.href = '/dashboard/admin';
        return Promise.reject(error);
      }
    }
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

/**
 * Re-reads the session on every page change inside the app.
 *
 * It lives here, inside the Router, because `useLocation` needs the router above it — App
 * renders the router itself, so it cannot watch the route on its own. Focus alone was not
 * enough: somebody who never leaves the tab (which is most people, most of the time) would keep
 * a stale sidebar while clicking around.
 */
const SessionRefresher = ({ onNavigate }) => {
  const { pathname } = useLocation();
  useEffect(() => { onNavigate(); }, [pathname, onNavigate]);
  return null;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastRefresh = useRef(0);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  /**
   * Re-read who this person is from the server.
   *
   * The session used to be read out of localStorage once at startup and never again, so anything
   * an admin changed — approving a storefront, granting Free-Comp, moving a tier — stayed
   * invisible in an already-open tab until a full reload or a fresh sign-in. The owner hit this
   * the first time he approved a real vendor: "It didn't open up the Vendor Hub for Joe. Is this
   * the same issue for everything else that doesn't change the instant it is accepted?" It was,
   * and it had nothing to do with the preview environment.
   *
   * Merged rather than replaced, because the stored object carries fields the endpoint doesn't
   * return, and only committed when something actually changed so the app doesn't re-render on a
   * timer for no reason.
   */
  const refreshUser = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    // Collapse a burst of focus/navigation events into one request — a window switcher held
    // down would otherwise fire /auth/me on every flick.
    const now = Date.now();
    if (now - lastRefresh.current < 400) return;
    lastRefresh.current = now;
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      if (!data?.id) return;
      setUser((current) => {
        const merged = { ...(current || {}), ...data };
        if (JSON.stringify(merged) === JSON.stringify(current)) return current;
        localStorage.setItem('user', JSON.stringify(merged));
        return merged;
      });
    } catch {
      // A failed refresh is not a reason to throw anyone out — the interceptor already handles
      // an expired token.
    }
  }, []);

  const hasSession = !!user;
  useEffect(() => {
    if (loading || !hasSession) return;
    refreshUser();
    const onFocus = () => refreshUser();
    const onVisible = () => { if (document.visibilityState === 'visible') refreshUser(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // Keyed on the session existing rather than on `user` itself: otherwise every refresh that
    // merges a change would tear these listeners down and set them up again.
  }, [loading, hasSession, refreshUser]);

  const login = useCallback((token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <AudioProvider>
      <LanguageProvider user={user}>
      <GlobalTranslator />
      <div className="App">
        <BrowserRouter>
          <SessionRefresher onNavigate={refreshUser} />
          <MusicPlayer />
          <MobileWelcomeGate />
          <PendingDeletionGate />
          <Routes>
            <Route path="/" element={!user ? <HomePage onLogin={login} /> : <Navigate to="/dashboard/portal" />} />
            
            {/* Google OAuth Callback */}
            <Route path="/auth/callback" element={<AuthCallback onLogin={login} />} />
            <Route path="/auth/link-google" element={<LinkGoogleCallback />} />
            <Route path="/practitioner-invite" element={<PractitionerInvitePage />} />
            <Route path="/vendor-invite" element={<VendorInvitePage />} />
            <Route path="/intake/:token" element={<IntakeFormFillPage />} />
            <Route path="/proposal/:token" element={<BookingProposalPage />} />
            <Route path="/events/:eventId" element={<EventDetailPage />} />
            <Route path="/print/intake/:responseId" element={<PrintIntakePage />} />
            <Route path="/practitioner-invite/google" element={<InviteGoogleCallback />} />
            
            {/* Email Verification Route */}
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            
            {/* Password Reset Routes */}
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* Public advertising: rate card, and the advertiser's own private build link */}
            <Route path="/advertise" element={<AdvertisePage />} />
            <Route path="/advertise/:token" element={<AdvertiserFormPage />} />
            {/* Old reactivation page (removed Jun 2026) — anyone with the link lands home */}
            <Route path="/reactivate" element={<Navigate to="/" replace />} />
            

            {/* Welcome Back interstitial (mobile cold-start; testable on web) */}
            <Route path="/welcome-back" element={user ? <WelcomeBack user={user} /> : <Navigate to="/" />} />
            
            {/* Payment Routes */}
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            
            {/* Legal Pages - Public Routes */}
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
            <Route path="/vendor-terms" element={<VendorTermsOfService />} />
            <Route path="/marketplace-guidelines" element={<MarketplaceGuidelines />} />
            <Route path="/practitioner-agreement" element={<PractitionerAgreementDoc />} />
            
            {/* Public Blog Route - No authentication required */}
            <Route path="/blog/:slug" element={<PublicBlogPage />} />
            
            {/* Public Booking Page - No authentication required */}
            <Route path="/booking/:slug" element={<PublicBookingPage />} />

            {/* Where an external booking platform (Calendly, Acuity…) returns the client after
                they book. The Booking Modes tab has always told practitioners to point their
                platform's redirect here, but the route was never registered — so the one page a
                client sees after booking was "This path doesn't exist yet". Public on purpose:
                a client who books through a practitioner's own link may never have logged in. */}
            <Route path="/booking-complete" element={<BookingConfirmation isExternal />} />
            
            <Route
              path="/dashboard/*"
              element={
                user ? (
                  <DashboardLayout user={user} onLogout={logout}>
                    <Routes>
                      {/* Bare /dashboard rendered the chrome with an empty middle for
                          every account — send it to the portal instead. */}
                      <Route index element={<Navigate to="/dashboard/portal" replace />} />
                      <Route path="portal" element={<PortalLanding user={user} />} />
                      <Route path="platinum-plus" element={<PlatinumPlus user={user} />} />
                      <Route path="modules" element={<ModulesPage user={user} />} />
                      <Route path="modules/:moduleId" element={<ModuleDetailPage user={user} />} />
                      <Route path="assessments" element={<CompanionPage />} />
                      <Route path="companion" element={<CompanionSpace />} />
                      <Route path="companion/:view" element={<CompanionSpace />} />
                      <Route path="assessments/list" element={<MyAssessmentsPage />} />
                      <Route path="participants" element={<Navigate to="/dashboard/practitioner-hub?tab=participants" replace />} />
                      <Route path="assessments/:assessmentId/take" element={<AssessmentTakePage />} />
                      <Route path="assessments/results/:attemptId" element={<AssessmentResultPage />} />
                      <Route path="section/:folderId/homework" element={<SectionHomeworkFeed />} />
                      <Route path="workshops" element={<WorkshopsPage user={user} />} />
                      <Route path="forum" element={<ForumPage user={user} />} />
                      <Route path="calendar" element={<CalendarGridPage user={user} />} />
                      <Route path="calendar/:date" element={<DayEventsPage user={user} />} />
                      <Route path="resources" element={<ResourcesPage user={user} />} />
                      <Route path="live-stream" element={<LiveStreamPage user={user} />} />
                      <Route path="replays" element={<ReplayArchivePage />} />                      <Route path="messages" element={<MessagesPage user={user} />} />
                      <Route path="settings" element={<SettingsPage user={user} />} />
              <Route path="questions" element={<QAPage />} />
                      <Route path="membership" element={<MyMembership />} />
                      <Route path="bookings" element={<BookingsPage user={user} />} />
                      <Route path="my-bookings" element={<MyBookings />} />
                      <Route path="bookings/review/:bookingId" element={<SessionReviewTip />} />
                      <Route path="practitioner-hub" element={<PractitionerHub user={user} />} />
                      <Route path="practitioner-onboarding" element={<PractitionerOnboarding />} />
                      <Route path="vendor-onboarding" element={<VendorOnboarding />} />
                      <Route path="intake-forms" element={<IntakeFormsPage />} />
                      {user.role === 'admin' && <Route path="admin" element={<AdminPanel user={user} />} />}
                      
                      {/* Marketplace Routes */}
                      <Route path="marketplace" element={<MarketplaceHome onNavigate={(view) => {}} />} />
                      <Route path="marketplace/bookings" element={<BookingsPortal user={user} />} />
                      <Route path="marketplace/orders" element={<OrderHistory />} />
                      {/* Land them IN the marketplace when they're done, not back where they came
                          from — history.back() returned an applicant to the onboarding page, which
                          then told them to apply. His words: "This is a redundancy." */}
                      <Route path="marketplace/vendor/apply" element={<VendorApplication onBack={() => { window.location.href = '/dashboard/marketplace'; }} onComplete={() => {}} />} />
                      <Route path="marketplace/vendor/products" element={<VendorProducts />} />
                      <Route path="marketplace/vendor/stripe" element={<VendorStripeOnboarding vendor={{}} user={user} />} />
                      <Route path="marketplace/vendor/dashboard" element={<Navigate to="/dashboard/practitioner-hub" replace />} />
                      <Route path="marketplace/vendor/earnings" element={<VendorEarnings />} />
                      <Route path="marketplace/vendor/orders-mgmt" element={<VendorOrders />} />
                      <Route path="marketplace/vendor/shipping" element={<VendorShipping />} />
                      <Route path="marketplace/vendor/availability" element={<VendorAvailability />} />
                      <Route path="marketplace/vendor/bookings-mgmt" element={<VendorBookings />} />
                      <Route path="marketplace/booking-confirmation/:bookingId" element={<BookingConfirmation />} />
                      <Route path="marketplace/vendor/reviews" element={<VendorReviews />} />
                      <Route path="marketplace/store/:slug" element={<VendorStorefront />} />
                      <Route path="marketplace/vendors" element={<AllVendorsPage />} />
                      <Route path="marketplace/product/:productId" element={<ProductDetail />} />
                      <Route path="marketplace/shelf/:slug" element={<ShelfPage />} />
                      <Route path="marketplace/cart" element={<CartPage />} />
                      {user.role === 'admin' && <Route path="marketplace/admin/vendors" element={<VendorApprovals />} />}
                      {user.role === 'admin' && <Route path="marketplace/admin/dashboard" element={<AdminDashboard />} />}

                      {/* Unknown dashboard URL — branded 404 inside the chrome (B1) */}
                      <Route path="*" element={<NotFoundPage variant="inline" />} />
                    </Routes>
                  </DashboardLayout>
                ) : (
                  <Navigate to="/" />
                )
              }
            />

            {/* Unknown public URL — branded 404 instead of a blank page (B1) */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </div>
      </LanguageProvider>
    </AudioProvider>
  );
}

export default App;